/**
 * XR Input Handler
 *
 * Handles WebXR controller inputs for RSVP reading:
 * - Trigger: play/pause
 * - Thumbstick up/down: WPM ±25 (250ms debounce)
 * - A/B buttons: rewind/restart
 * - X/Y buttons: fast-forward +10
 *
 * Designed for Quest controllers but works with any XR input source.
 */

import type { ReaderCore } from '../../core/readerCore';
import { PACING_CONFIG } from '../../core/pacing';

/**
 * Input debounce configuration
 */
const INPUT_CONFIG = {
  /** Debounce time for WPM changes in ms */
  WPM_DEBOUNCE_MS: 250,
  /** Thumbstick threshold for triggering actions */
  THUMBSTICK_THRESHOLD: 0.5,
  /** WPM change amount per input */
  WPM_STEP: PACING_CONFIG.WPM_STEP,
} as const;

/**
 * Gamepad button indices for standard XR controllers
 * Based on WebXR Gamepad standard mapping
 */
const BUTTON_INDEX = {
  TRIGGER: 0,
  SQUEEZE: 1,
  TOUCHPAD: 2,
  THUMBSTICK: 3,
  X_A: 4,  // X on left, A on right
  Y_B: 5,  // Y on left, B on right
} as const;

/**
 * Gamepad axes indices
 */
const AXES_INDEX = {
  THUMBSTICK_X: 2,
  THUMBSTICK_Y: 3,
} as const;

/**
 * Track button states for edge detection
 */
interface ButtonState {
  trigger: boolean;
  xA: boolean;
  yB: boolean;
}

/**
 * XR Input Handler class
 */
export class XRInputHandler {
  private core: ReaderCore;
  private leftState: ButtonState = { trigger: false, xA: false, yB: false };
  private rightState: ButtonState = { trigger: false, xA: false, yB: false };
  private lastWpmChangeTime: number = 0;
  private isActive: boolean = false;

  constructor(core: ReaderCore) {
    this.core = core;
  }

  /**
   * Process XR input sources each frame
   * Call this from the XR render loop
   *
   * @param session - The active XR session
   * @param _frame - The XR frame (reserved for future pose-based input)
   */
  processInput(session: XRSession, _frame: XRFrame): void {
    if (!this.isActive) return;

    for (const inputSource of session.inputSources) {
      if (!inputSource.gamepad) continue;

      const isLeft = inputSource.handedness === 'left';
      const state = isLeft ? this.leftState : this.rightState;

      this.processGamepad(inputSource.gamepad, state, isLeft);
    }
  }

  /**
   * Process a single gamepad's input
   */
  private processGamepad(gamepad: Gamepad, state: ButtonState, isLeft: boolean): void {
    const buttons = gamepad.buttons;
    const axes = gamepad.axes;

    // Trigger: play/pause (on press, not hold)
    const triggerPressed = buttons[BUTTON_INDEX.TRIGGER]?.pressed ?? false;
    if (triggerPressed && !state.trigger) {
      this.core.togglePlayPause();
    }
    state.trigger = triggerPressed;

    // X/A button: rewind (left = X, right = A)
    const xAPressed = buttons[BUTTON_INDEX.X_A]?.pressed ?? false;
    if (xAPressed && !state.xA) {
      this.core.rewind();
    }
    state.xA = xAPressed;

    // Y/B button: fast-forward (left = Y, right = B) or restart on left controller
    const yBPressed = buttons[BUTTON_INDEX.Y_B]?.pressed ?? false;
    if (yBPressed && !state.yB) {
      if (isLeft) {
        // Left Y = restart
        this.core.restart();
      } else {
        // Right B = fast-forward
        this.core.fastForward();
      }
    }
    state.yB = yBPressed;

    // Thumbstick up/down: WPM adjustment with debounce
    const thumbstickY = axes[AXES_INDEX.THUMBSTICK_Y] ?? 0;
    this.processThumbstickWpm(thumbstickY);
  }

  /**
   * Process thumbstick Y-axis for WPM adjustment
   * Positive Y = up = increase WPM
   * Negative Y = down = decrease WPM
   */
  private processThumbstickWpm(yValue: number): void {
    // Check debounce
    const now = performance.now();
    if (now - this.lastWpmChangeTime < INPUT_CONFIG.WPM_DEBOUNCE_MS) {
      return;
    }

    // Check threshold
    if (Math.abs(yValue) < INPUT_CONFIG.THUMBSTICK_THRESHOLD) {
      return;
    }

    // Apply WPM change
    // Note: WebXR thumbstick Y is inverted (up = negative)
    const delta = yValue < 0 ? INPUT_CONFIG.WPM_STEP : -INPUT_CONFIG.WPM_STEP;
    this.core.adjustWpm(delta);
    this.lastWpmChangeTime = now;
  }

  /**
   * Start processing input
   */
  start(): void {
    this.isActive = true;
    this.resetState();
  }

  /**
   * Stop processing input
   */
  stop(): void {
    this.isActive = false;
    this.resetState();
  }

  /**
   * Reset button states
   */
  private resetState(): void {
    this.leftState = { trigger: false, xA: false, yB: false };
    this.rightState = { trigger: false, xA: false, yB: false };
    this.lastWpmChangeTime = 0;
  }

  /**
   * Check if handler is active
   */
  get active(): boolean {
    return this.isActive;
  }
}

/**
 * Create an XR input handler for a ReaderCore
 */
export function createXRInputHandler(core: ReaderCore): XRInputHandler {
  return new XRInputHandler(core);
}
